import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useLocation } from 'react-router-dom';
import { Button } from 'src/components/ui/button';
import {
  Mail,
  Phone,
  Loader2,
  MapPin,
  GraduationCap,
  Briefcase,
  Award,
  UserCheck,
  Building2,
  IdCard,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  Wallet,
  Clock,
  User,
  FileDown,
  History,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Checkbox } from 'src/components/ui/checkbox';
import { format, parse } from 'date-fns';
import { useToast } from 'src/hooks/use-toast';
import { PageHeader } from 'src/components/common/PageHeader';
import JoiningDetailsModal from 'src/components/Modals/JoiningDetailsModal';
import { CandidateService } from 'src/services/api/CandidateService';
import {
  useCandidateProfile,
  useAuditConfig,
  useCandidateAuditLogs,
} from 'src/services/api/queries/candidateQueries';
import { useVerifyJoiningDetails } from 'src/services/api/mutations/candidateMutations';
import SkillPill from 'src/components/common/SkillPill';
import SearchInput from 'src/components/common/SearchInput';
import AppSelect from 'src/components/common/AppSelect';
import { CANDIDATE_MAPPING_STATUS_VALUES } from 'src/utils/statusConstants';
import {
  useLocationMap,
  useStateMap,
} from 'src/services/api/queries/referenceDataQueries';
import { sortWorkExperiences } from 'src/lib/workExperience';
import { useAuth } from 'src/hooks/useAuth';

const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const normalizeYear = (y) => {
  const n = parseInt(y, 10);
  if (!n) return n;
  if (n < 100) return n <= 50 ? 2000 + n : 1900 + n;
  return n;
};

const formatDateRange = (dateString) => {
  if (!dateString) return '';
  try {
    const parts = String(dateString).split('/');
    if (parts.length === 2) {
      // legacy format: m/YY
      const mi = parseInt(parts[0], 10);
      const fullYear = normalizeYear(parts[1]);
      if (mi < 1 || mi > 12 || !fullYear) return dateString;
      return `${MONTHS_SHORT[mi - 1]} ${fullYear}`;
    }
    if (parts.length === 3) {
      // full format: DD/MM/YYYY
      const date = parse(
        `${parts[0]}/${parts[1]}/${parts[2]}`,
        'dd/MM/yyyy',
        new Date()
      );
      return format(date, 'MMM yyyy');
    }
    return dateString;
  } catch {
    return dateString;
  }
};

const parseWorkDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    const parts = String(dateStr).split('/');
    const d = new Date(0);
    if (parts.length === 2) {
      const m = parseInt(parts[0], 10);
      const y = normalizeYear(parts[1]);
      if (!m || !y || m < 1 || m > 12) return null;
      d.setFullYear(y, m - 1, 1);
    } else if (parts.length === 3) {
      const m = parseInt(parts[1], 10);
      const y = parseInt(parts[2], 10);
      if (!m || !y || m < 1 || m > 12) return null;
      d.setFullYear(y, m - 1, parseInt(parts[0], 10) || 1);
    } else {
      return null;
    }
    return d;
  } catch {
    return null;
  }
};

const calcTenure = (fromDate, toDate, isCurrent) => {
  const start = parseWorkDate(fromDate);
  if (!start) return null;
  const end = isCurrent ? new Date() : parseWorkDate(toDate);
  if (!end) return null;
  const months =
    (end.getFullYear() - start.getFullYear()) * 12 +
    (end.getMonth() - start.getMonth());
  if (months < 0 || months > 720) return null;
  if (months < 12) return `${months}m`;
  const yrs = Math.floor(months / 12);
  const rem = months % 12;
  return rem > 0 ? `${yrs}y ${rem}m` : `${yrs}y`;
};

const formatCTC = (amount) => {
  if (!amount) return null;
  const n = Number(amount);
  if (Number.isNaN(n) || n === 0) return null;
  if (n >= 10000000) {
    const cr = n / 10000000;
    return `₹${cr % 1 === 0 ? cr : cr.toFixed(1)}Cr`;
  }
  if (n >= 100000) {
    const l = n / 100000;
    return `₹${l % 1 === 0 ? l : l.toFixed(1)}L`;
  }
  return `₹${n.toLocaleString('en-IN')}`;
};

const Preview = () => {
  const locationMap = useLocationMap();
  const stateMap = useStateMap();
  const { id } = useParams();

  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status');
  const opportunityId = searchParams.get('opportunityId');
  const requirementLocationId = searchParams.get('requirementLocationId');
  const assignmentId = searchParams.get('assignmentId');
  const { getUserInfo } = useAuth();
  const isCseUser = !!getUserInfo()?.roles?.includes('ahedge_cse');

  const {
    data: profileResponse,
    isLoading,
    isError,
    error,
    refetch: refetchProfile,
  } = useCandidateProfile(id, opportunityId, { enabled: !!id });
  const verifyJoining = useVerifyJoiningDetails();
  const candidateData = profileResponse?.success
    ? profileResponse.data.profile_data
    : null;
  const regId = profileResponse?.success
    ? profileResponse.data.registration_id
    : null;
  const joiningInfo = candidateData?.joining_info || null;

  const [showJoiningModal, setShowJoiningModal] = useState(false);
  const [showPdfDialog, setShowPdfDialog] = useState(false);
  const [pdfMasked, setPdfMasked] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const sectionRefs = useRef({});
  const [expandedDuties, setExpandedDuties] = useState(new Set());

  // Profile / Audit Log tabs.
  const [activeTab, setActiveTab] = useState('profile');
  const [auditSearch, setAuditSearch] = useState('');
  const [debouncedAuditSearch, setDebouncedAuditSearch] = useState('');
  const [eventCategory, setEventCategory] = useState('');
  const [actorType, setActorType] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [auditPage, setAuditPage] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedAuditSearch(auditSearch), 350);
    return () => clearTimeout(timer);
  }, [auditSearch]);

  useEffect(() => {
    setAuditPage(1);
  }, [debouncedAuditSearch, eventCategory, actorType, sortBy]);

  const { data: auditConfigResponse } = useAuditConfig({
    enabled: activeTab === 'audit',
  });
  const auditConfig = auditConfigResponse?.success
    ? auditConfigResponse.data.audit_configs || {}
    : {};
  const { data: auditLogsResponse, isFetching: auditLogsLoading } =
    useCandidateAuditLogs({
      candidateId: id,
      page: auditPage,
      search: debouncedAuditSearch,
      eventCategory,
      actorType,
      sortBy,
      enabled: activeTab === 'audit',
    });
  const auditLogs = auditLogsResponse?.success
    ? auditLogsResponse.data.audit_logs || []
    : [];
  const auditPagination = auditLogsResponse?.success
    ? auditLogsResponse.data.pagination || {}
    : {};

  const formatAuditDate = (timestamp) => {
    if (!timestamp) return '';
    try {
      return format(new Date(timestamp), 'dd MMM yyyy, hh:mm a');
    } catch {
      return timestamp;
    }
  };

  const getEventIcon = (eventLabel) =>
    eventLabel ? eventLabel.charAt(0).toUpperCase() : 'I';

  // const [showScore, setShowScore] = useState(true);
  const location = useLocation();
  const origin = location.state?.from;

  // Breadcrumb trail adapts to where the user came from (Search, Upload
  // Tracker, or the submitted-candidates list), preserving the source's
  // query/state so the back-links restore the previous view.
  const breadcrumbItems = (() => {
    const home = { label: 'Home', href: '/' };
    const profile = { label: 'Profile', href: `/candidates/${id}` };

    if (origin === 'upload-tracker') {
      const { jobRef, jobName, trackerQuery, dateMode, dateFrom, dateTo } =
        location.state ?? {};
      const jobHref = jobRef
        ? `/upload-tracker/${encodeURIComponent(jobRef)}${
            trackerQuery ? `?${trackerQuery}` : ''
          }`
        : '/upload-tracker';
      return [
        home,
        { label: 'Upload Tracker', href: '/upload-tracker' },
        {
          label: jobName || jobRef,
          href: jobHref,
          state: { dateMode, dateFrom, dateTo },
        },
        profile,
      ];
    }

    if (origin === 'search') {
      const searchQuery = location.state?.searchQuery;
      return [
        home,
        {
          label: 'Search Candidates',
          href: searchQuery
            ? `/candidates/search?${searchQuery}`
            : '/candidates/search',
        },
        profile,
      ];
    }

    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (opportunityId) params.set('opportunityId', opportunityId);
    if (requirementLocationId)
      params.set('requirementLocationId', requirementLocationId);
    if (assignmentId) params.set('assignmentId', assignmentId);
    const qs = params.toString();

    // TA's assignment pipeline (AssignmentPipeline.jsx) — a distinct page
    // from CSE's /candidates/list (RequirementPipeline.jsx) below, even though
    // RowActionsCell.jsx passes the exact same status/opportunityId/
    // assignmentId/requirementLocationId query params here. The `from` flag
    // disambiguates on a same-tab navigate, but a target="_blank" "Full page"
    // link (CandidateProfileModal.jsx) can never carry router state, so a TA
    // opening the link in a new tab would otherwise fall through to the
    // CSE-only /candidates/list below and 403. Falling back to role (a TA is
    // never meant to land on RequirementPipeline at all) covers that case too.
    if (origin === 'assignment-pipeline' || (!isCseUser && assignmentId)) {
      return [
        home,
        {
          label: 'Screening Pipeline',
          href: qs ? `/assignment-pipeline?${qs}` : '/assignment-pipeline',
        },
        profile,
      ];
    }

    return [
      home,
      ...(opportunityId
        ? [{ label: 'Opportunities', href: '/opportunities' }]
        : []),
      {
        label: 'Candidates',
        href: qs ? `/candidates/list?${qs}` : '/candidates/list',
      },
      profile,
    ];
  })();

  const getQualificationDisplayName = (qualificationType) => {
    switch (qualificationType) {
      case 'TENTH':
        return 'Secondary';
      case 'TWELFTH':
        return 'Higher secondary';
      case 'DEGREE':
        return "Bachelor's Degree";
      case 'MASTERS':
        return "Master's Degree";
      default:
        return qualificationType;
    }
  };

  const scrollToSection = (section) => {
    setActiveSection(section);
    const el = sectionRefs.current[section];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const toggleDuties = (key) => {
    setExpandedDuties((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    const sections = [
      'overview',
      'experience',
      'education',
      'skills',
      'certifications',
    ];
    const observers = sections.map((section) => {
      const el = sectionRefs.current[section];
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveSection(section);
        },
        { rootMargin: '-30% 0px -60% 0px', threshold: 0 }
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach((obs) => obs && obs.disconnect());
  }, [candidateData, activeTab]);

  const formatScore = (score) => {
    if (!score) return '';
    const numScore = Number(score);
    if (Number.isInteger(numScore)) {
      return numScore.toString();
    }
    return numScore.toString();
  };

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    // Open the tab synchronously, still inside the click's user-gesture
    // window — the fetch below is async, so opening after it resolves would
    // get blocked as a popup by most browsers.
    const newTab = window.open('', '_blank');
    try {
      const blob = await CandidateService.downloadResumePdf(id, pdfMasked);
      const url = URL.createObjectURL(blob);
      if (newTab) {
        newTab.location.href = url;
      } else {
        // Popup blocked (e.g. gesture window already expired) — fall back to
        // asking again, this time with the URL ready.
        window.open(url, '_blank');
      }
      // Revoke once the tab has had time to load the PDF into memory.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      setShowPdfDialog(false);
      setPdfMasked(false);
    } catch (err) {
      if (newTab) newTab.close();
      toast({
        title: 'Error',
        description: err.message || 'Failed to download resume PDF',
        variant: 'destructive',
      });
    } finally {
      setPdfLoading(false);
    }
  };

  const handleJoiningDetailsSubmit = (joiningData) => {
    const payload = {
      ...joiningData,
      requirement_location_id: requirementLocationId,
    };

    verifyJoining.mutate(payload, {
      onSuccess: (response) => {
        if (response.success) {
          toast({
            title: 'Success',
            description: 'Joining details saved successfully',
          });
          setShowJoiningModal(false);
          refetchProfile();
        }
      },
      onError: (err) => {
        toast({
          title: 'Error',
          description: err.message || 'Failed to save joining details',
          variant: 'destructive',
        });
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-muted">
        <Loader2 className="h-8 w-8 animate-spin ahBlueText" />
        <p className="mt-4 text-sm text-muted-foreground">
          Loading candidate data...
        </p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-muted">
        <p className="text-destructive">
          {error?.message || 'Failed to load candidate details'}
        </p>
      </div>
    );
  }

  if (!candidateData) {
    return <div>No candidate data found.</div>;
  }

  const {
    personal_information: personalInfo,
    educational_details: educationalDetails,
    work_experiences: rawWorkExperiences,
    skills,
    certifications,
  } = candidateData;

  const workExperiences = sortWorkExperiences(rawWorkExperiences);

  // ── pre-compute derived values ────────────────────────────────────────────
  const currentExp =
    workExperiences.find((e) => e.is_current) || workExperiences[0];
  const heroInitials =
    `${personalInfo.first_name?.[0] || ''}${personalInfo.last_name?.[0] || ''}`.toUpperCase();
  const heroFullName = [personalInfo.first_name, personalInfo.last_name]
    .filter(Boolean)
    .join(' ');
  const heroLocation = [
    personalInfo.address?.city_name ||
      (personalInfo.address?.city
        ? locationMap[personalInfo.address.city] || personalInfo.address.city
        : null),
    personalInfo.address?.state_name ||
      (personalInfo.address?.state
        ? stateMap[personalInfo.address.state] || personalInfo.address.state
        : null),
  ]
    .filter(Boolean)
    .join(', ');

  const od = candidateData?.other_details || {};
  const totalExpMonths = od.total_work_experience;
  const totalExpLabel = (() => {
    if (!totalExpMonths) return null;
    const y = Math.floor(totalExpMonths / 12);
    const m = totalExpMonths % 12;
    const parts = [];
    if (y) parts.push(`${y} yr${y > 1 ? 's' : ''}`);
    if (m) parts.push(`${m} mo`);
    return parts.join(' ') || null;
  })();

  const skillsCount = (skills || []).length;

  const domainSkills = (skills || []).filter(
    (s) => !s.type || s.type === 'DOMAIN_SKILL'
  );
  const toolSkills = (skills || []).filter((s) => s.type === 'TOOL');
  const regSkills = (skills || []).filter((s) => s.type === 'REGULATORY');
  const hasSkillTypes = (skills || []).some((s) => s.type);

  const latestEdu = educationalDetails?.length
    ? [...educationalDetails].sort(
        (a, b) => (b.graduation_year || 0) - (a.graduation_year || 0)
      )[0]
    : null;

  const snapshotRows = [
    currentExp?.company_name
      ? {
          icon: Building2,
          label: 'Current employer',
          value: currentExp.company_name,
        }
      : null,
    currentExp?.role
      ? { icon: Briefcase, label: 'Current role', value: currentExp.role }
      : null,
    latestEdu
      ? {
          icon: GraduationCap,
          label: 'Highest qualification',
          value: getQualificationDisplayName(latestEdu.qualification_type),
          sub: latestEdu.graduation_year
            ? `· ${latestEdu.graduation_year}`
            : null,
        }
      : null,
    personalInfo.notice_period != null
      ? {
          icon: Clock,
          label: 'Notice period',
          value: `${personalInfo.notice_period} days`,
        }
      : null,
    formatCTC(personalInfo.current_ctc)
      ? {
          icon: Wallet,
          label: 'Current CTC',
          value: formatCTC(personalInfo.current_ctc),
        }
      : null,
    formatCTC(personalInfo.expected_ctc)
      ? {
          icon: TrendingUp,
          label: 'Expected CTC',
          value: formatCTC(personalInfo.expected_ctc),
        }
      : null,
    personalInfo.preferred_work_locations?.length > 0
      ? {
          icon: MapPin,
          label: 'Preferred Locations',
          value: personalInfo.preferred_work_locations
            .map((locId) => locationMap[locId] || locId)
            .join(', '),
        }
      : null,
    personalInfo.email
      ? { icon: Mail, label: 'Email', value: personalInfo.email }
      : null,
    personalInfo.phone
      ? { icon: Phone, label: 'Phone', value: personalInfo.phone }
      : null,
  ].filter(Boolean);

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div>
      <PageHeader
        breadcrumbs={breadcrumbItems}
        actions={
          <>
            {status && (
              <span className="inline-flex items-center px-2.5 py-[4px] rounded-full bg-muted border border-border text-[11px] font-[600] tracking-[0.04em] text-muted-foreground uppercase">
                {CANDIDATE_MAPPING_STATUS_VALUES[status]}
              </span>
            )}
            <Button
              variant="outline"
              size="headerSm"
              className="gap-1.5"
              onClick={() => setShowPdfDialog(true)}
              disabled={pdfLoading}
            >
              {pdfLoading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <FileDown className="w-3.5 h-3.5" strokeWidth={1.8} />
              )}
              Export PDF
            </Button>
          </>
        }
      />
      <div className="min-h-[calc(100vh-3.4rem)] bg-muted">
        <div className="max-w-[1120px] mx-auto px-4 lg:px-7 py-6">
          <div className="flex items-center mb-5">
            {[
              { key: 'profile', label: 'Profile', icon: User, count: null },
              {
                key: 'audit',
                label: 'Audit Log',
                icon: History,
                count: auditPagination.total ?? null,
              },
            ].map(({ key, label, icon: Icon, count }) => {
              const isActive = activeTab === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveTab(key)}
                  className={`flex items-center gap-2 px-[18px] py-[10px] text-[13px] border-b-2 -mb-px transition-colors ${
                    isActive
                      ? 'font-semibold border-primary text-primary'
                      : 'font-medium border-transparent text-muted-foreground hover:text-foreground-strong'
                  }`}
                >
                  <Icon className="w-[14px] h-[14px]" />
                  {label}
                  {count != null && (
                    <span
                      className={`text-[11px] font-semibold px-[7px] py-[1px] rounded-full ${
                        isActive
                          ? 'bg-brand-bg-subtle text-primary'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {activeTab === 'profile' && (
            <>
              {/* ── Hero card ── */}
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                {/* Banner */}
                <div
                  style={{
                    height: 74,
                    background:
                      'linear-gradient(178deg,#1B6AAF 0%,#155A94 35%,#0E4170 70%,#0A3058 100%)',
                  }}
                  className="relative overflow-hidden"
                >
                  <div
                    className="absolute inset-0"
                    style={{
                      background:
                        'radial-gradient(ellipse at 60% 50%,rgba(255,255,255,.08) 0%,transparent 70%)',
                    }}
                  />
                </div>

                {/* Hero body */}
                <div className="flex gap-5 items-start px-[26px] pb-[22px]">
                  {/* Avatar */}
                  <div
                    style={{
                      width: 96,
                      height: 96,
                      marginTop: -40,
                      background: 'linear-gradient(135deg,#2068B2,#1553A0)',
                      fontSize: 28,
                      letterSpacing: -1,
                    }}
                    className="rounded-[22px] border-[4px] border-card shadow-[0_4px_18px_rgba(0,0,0,.14)] flex-shrink-0 flex items-center justify-center text-white font-bold select-none z-[2]"
                  >
                    {heroInitials}
                  </div>

                  {/* Name / role / meta */}
                  <div className="flex-1 min-w-0 pt-[14px]">
                    <div className="text-[25px] font-bold tracking-[-0.035em] text-foreground leading-[1.15]">
                      {heroFullName}
                    </div>
                    {(currentExp?.role || currentExp?.company_name) && (
                      <div className="text-[15px] text-muted-foreground mt-[5px]">
                        {currentExp?.role}
                        {currentExp?.role && currentExp?.company_name && (
                          <span className="mx-[6px] opacity-50">·</span>
                        )}
                        {currentExp?.company_name}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-4 mt-[14px]">
                      {heroLocation && (
                        <span className="inline-flex items-center gap-[5px] text-[12.5px] text-muted-foreground">
                          <MapPin size={13} className="opacity-60" />
                          {heroLocation}
                        </span>
                      )}
                      {totalExpLabel && (
                        <span className="inline-flex items-center gap-[5px] text-[12.5px] text-muted-foreground">
                          <Briefcase size={13} className="opacity-60" />
                          {totalExpLabel} experience
                        </span>
                      )}
                      {personalInfo.email && (
                        <span className="inline-flex items-center gap-[5px] text-[12.5px] text-muted-foreground">
                          <Mail size={13} className="opacity-60" />
                          {personalInfo.email}
                        </span>
                      )}
                      {personalInfo.phone && (
                        <span className="inline-flex items-center gap-[5px] text-[12.5px] text-muted-foreground">
                          <Phone size={13} className="opacity-60" />
                          {personalInfo.phone}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Right: reg ID + action */}
                  <div
                    style={{ width: 248, minWidth: 248 }}
                    className="pt-4 flex flex-col items-end gap-[10px]"
                  >
                    {regId && (
                      <span className="inline-flex items-center gap-[6px] rounded-full bg-brand-bg-subtle border border-primary/15 px-[11px] py-[5px] text-[12px] font-semibold text-primary">
                        <IdCard size={13} />
                        {regId}
                      </span>
                    )}
                    {status === 'joined' &&
                      !joiningInfo?.joining_date_verified && (
                        <button
                          type="button"
                          onClick={() => setShowJoiningModal(true)}
                          className="inline-flex items-center gap-[6px] rounded-[8px] bg-[#0D9668] text-white text-[12.5px] font-semibold px-[13px] py-[7px] border-none cursor-pointer"
                        >
                          <UserCheck size={13} />
                          Verify Joining
                        </button>
                      )}
                  </div>
                </div>
              </div>

              {/* ── Subnav ── */}
              <nav
                style={{ position: 'sticky', top: '50px', zIndex: 4 }}
                className="bg-muted/80 backdrop-blur-[8px] border-b border-border flex gap-[2px]"
              >
                {[
                  { secId: 'experience', label: 'Experience' },
                  { secId: 'skills', label: 'Skills' },
                  { secId: 'education', label: 'Education' },
                ].map(({ secId, label }) => (
                  <button
                    key={secId}
                    type="button"
                    onClick={() => scrollToSection(secId)}
                    style={{
                      borderBottom:
                        activeSection === secId
                          ? '2px solid currentColor'
                          : '2px solid transparent',
                    }}
                    className={`px-[14px] py-[11px] text-[13px] font-[550] bg-transparent border-l-0 border-r-0 border-t-0 cursor-pointer transition-colors ${
                      activeSection === secId
                        ? 'text-primary'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </nav>

              {/* ── Two-column grid ── */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0,1fr) 332px',
                  gap: 22,
                  marginTop: 22,
                }}
              >
                {/* Main column */}
                <div className="flex flex-col gap-4">
                  {/* Experience */}
                  <div
                    ref={(el) => {
                      sectionRefs.current.experience = el;
                    }}
                    id="section-experience"
                    className="bg-card border border-border rounded-xl p-[20px_22px]"
                  >
                    <div className="flex items-center gap-[9px] mb-[14px]">
                      <div className="w-[26px] h-[26px] rounded-[7px] bg-brand-bg-subtle text-primary flex items-center justify-center flex-shrink-0">
                        <Briefcase size={13} />
                      </div>
                      <h2 className="text-[16px] font-bold tracking-[-0.02em] text-foreground m-0">
                        Experience
                      </h2>
                      {workExperiences?.length > 0 && (
                        <span className="font-mono text-[11px] font-semibold text-muted-foreground bg-muted border border-border px-[7px] py-[1px] rounded-[20px] ml-auto">
                          {workExperiences.length}
                        </span>
                      )}
                    </div>
                    {workExperiences?.length > 0 ? (
                      <div>
                        {workExperiences.map((exp, idx) => {
                          const isCurrent = !!exp.is_current;
                          const tenure = calcTenure(
                            exp.from_date,
                            exp.to_date,
                            isCurrent
                          );
                          const rawDuties = exp.duties || [];
                          const sortedDuties = [...rawDuties].sort((a, b) => {
                            if (
                              a.type === 'ACHIEVEMENT' &&
                              b.type !== 'ACHIEVEMENT'
                            )
                              return -1;
                            if (
                              b.type === 'ACHIEVEMENT' &&
                              a.type !== 'ACHIEVEMENT'
                            )
                              return 1;
                            return 0;
                          });
                          const expKey = `exp-${exp.company_name || idx}`;
                          const isExpanded = expandedDuties.has(expKey);
                          const visibleDuties = isExpanded
                            ? sortedDuties
                            : sortedDuties.slice(0, 2);
                          const hiddenCount = sortedDuties.length - 2;
                          const toDateLabel = (() => {
                            if (isCurrent) return 'Present';
                            if (exp.to_date)
                              return formatDateRange(exp.to_date);
                            return '—';
                          })();
                          return (
                            <div
                              key={expKey}
                              className="relative pb-[22px] pl-[30px]"
                            >
                              {idx < workExperiences.length - 1 && (
                                <div className="absolute left-[7px] top-[20px] bottom-[-2px] w-[2px] bg-border" />
                              )}
                              <div
                                style={{
                                  position: 'absolute',
                                  left: 0,
                                  top: 4,
                                  width: 16,
                                  height: 16,
                                  borderRadius: '50%',
                                  border: isCurrent
                                    ? '3px solid #2068B2'
                                    : '3px solid hsl(var(--muted-foreground) / 0.5)',
                                  boxShadow: isCurrent
                                    ? '0 0 0 3px rgba(32,104,178,.12)'
                                    : '0 0 0 3px hsl(var(--muted))',
                                }}
                                className="bg-card"
                              />
                              <div className="flex items-start justify-between gap-3 mb-[10px]">
                                <div>
                                  <div className="text-[14px] font-[650] text-foreground leading-[1.3]">
                                    {exp.role || '—'}
                                  </div>
                                  <div className="flex items-center gap-[6px] mt-[3px] text-[12.5px] text-muted-foreground flex-wrap">
                                    <Building2
                                      size={12}
                                      className="opacity-60"
                                    />
                                    {exp.company_name}
                                    {isCurrent && (
                                      <span className="text-[9.5px] font-bold text-primary bg-brand-bg-subtle border border-primary/15 px-[7px] py-[1px] rounded-[20px] uppercase tracking-[0.04em]">
                                        Current
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="text-right flex-shrink-0 text-[12px] text-muted-foreground">
                                  <div>
                                    {formatDateRange(exp.from_date)} &ndash;{' '}
                                    {toDateLabel}
                                  </div>
                                  {tenure && (
                                    <div className="text-[11.5px] mt-[2px] font-semibold">
                                      {tenure}
                                    </div>
                                  )}
                                </div>
                              </div>
                              {exp.skills?.length > 0 &&
                                (() => {
                                  const skillsKey = `${expKey}-skills`;
                                  const skillsExpanded =
                                    expandedDuties.has(skillsKey);
                                  const SKILL_LIMIT = 4;
                                  const visibleSkills = skillsExpanded
                                    ? exp.skills
                                    : exp.skills.slice(0, SKILL_LIMIT);
                                  const hiddenSkillCount =
                                    exp.skills.length - SKILL_LIMIT;
                                  return (
                                    <div className="mb-[10px] flex flex-wrap gap-[6px] items-center">
                                      {visibleSkills.map((skill) => {
                                        const skillLabel =
                                          typeof skill === 'string'
                                            ? skill
                                            : skill?.skill_name ||
                                              skill?.name ||
                                              '';
                                        return (
                                          <SkillPill
                                            key={`${expKey}-skill-${skillLabel}`}
                                            label={skillLabel}
                                          />
                                        );
                                      })}
                                      {!skillsExpanded &&
                                        hiddenSkillCount > 0 && (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              toggleDuties(skillsKey)
                                            }
                                            className="text-[12px] font-semibold text-muted-foreground bg-muted border border-border rounded-[6px] px-[10px] py-[4px] cursor-pointer"
                                          >
                                            +{hiddenSkillCount} more
                                          </button>
                                        )}
                                      {skillsExpanded &&
                                        hiddenSkillCount > 0 && (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              toggleDuties(skillsKey)
                                            }
                                            className="text-[12px] font-semibold text-muted-foreground bg-transparent border-none px-[2px] py-[4px] cursor-pointer"
                                          >
                                            show less
                                          </button>
                                        )}
                                    </div>
                                  );
                                })()}
                              {visibleDuties.length > 0 && (
                                <div className="flex flex-col gap-[6px]">
                                  {visibleDuties.map((duty) => {
                                    const isAch = duty.type === 'ACHIEVEMENT';
                                    const dutyKey = `${duty.type || 'r'}-${(duty.description || '').slice(0, 30)}`;
                                    return (
                                      <div
                                        key={dutyKey}
                                        className={`rounded-[8px] px-[13px] py-[10px] ${
                                          isAch
                                            ? 'bg-ds-success-subtle border border-ds-success/20'
                                            : 'bg-card border border-border'
                                        }`}
                                      >
                                        <div
                                          className="flex items-center gap-[6px] flex-wrap"
                                          style={{
                                            marginBottom: duty.description
                                              ? 4
                                              : 0,
                                          }}
                                        >
                                          {isAch && (
                                            <span className="text-[9.5px] font-bold uppercase tracking-[0.04em] px-[6px] py-[2px] rounded-[4px] bg-ds-success text-white flex-shrink-0">
                                              Achievement
                                            </span>
                                          )}
                                          {duty.title && (
                                            <span className="text-[12.8px] font-[650] text-foreground">
                                              {duty.title}
                                            </span>
                                          )}
                                          {isAch && duty.impact && (
                                            <span className="inline-flex items-center gap-[3px] text-[11.5px] font-[650] text-ds-success mr-auto">
                                              <TrendingUp size={11} />
                                              {duty.impact}
                                            </span>
                                          )}
                                        </div>
                                        {duty.description && (
                                          <p className="text-[12.8px] m-0 leading-[1.55] text-muted-foreground">
                                            {duty.description}
                                          </p>
                                        )}
                                      </div>
                                    );
                                  })}
                                  {sortedDuties.length > 2 && (
                                    <button
                                      type="button"
                                      onClick={() => toggleDuties(expKey)}
                                      className="inline-flex items-center gap-[4px] text-[12px] font-semibold text-primary bg-transparent border-none cursor-pointer py-[4px] self-start"
                                    >
                                      {isExpanded ? (
                                        <>
                                          <ChevronUp size={13} /> Show less
                                        </>
                                      ) : (
                                        <>
                                          <ChevronDown size={13} /> Show{' '}
                                          {hiddenCount} more
                                        </>
                                      )}
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-[13px] text-muted-foreground text-center py-[20px] m-0">
                        No work experience added yet
                      </p>
                    )}
                  </div>

                  {/* Education */}
                  <div
                    ref={(el) => {
                      sectionRefs.current.education = el;
                    }}
                    id="section-education"
                    className="bg-card border border-border rounded-xl p-[20px_22px]"
                  >
                    <div className="flex items-center gap-[9px] mb-[14px]">
                      <div className="w-[26px] h-[26px] rounded-[7px] bg-brand-bg-subtle text-primary flex items-center justify-center flex-shrink-0">
                        <GraduationCap size={13} />
                      </div>
                      <h2 className="text-[16px] font-bold tracking-[-0.02em] text-foreground m-0">
                        Education
                      </h2>
                      {educationalDetails?.length > 0 && (
                        <span className="font-mono text-[11px] font-semibold text-muted-foreground bg-muted border border-border px-[7px] py-[1px] rounded-[20px] ml-auto">
                          {educationalDetails.length}
                        </span>
                      )}
                    </div>
                    {educationalDetails?.length > 0 ? (
                      <div>
                        {[...educationalDetails]
                          .sort(
                            (a, b) =>
                              (b.graduation_year || 0) -
                              (a.graduation_year || 0)
                          )
                          .map((edu) => {
                            const eduKey = `${edu.institution}-${edu.graduation_year}-${edu.qualification_type}`;
                            return (
                              <div
                                key={eduKey}
                                className="flex gap-3 py-[12px] border-b border-border last:border-b-0"
                              >
                                <div className="w-[38px] h-[38px] rounded-[10px] bg-brand-bg-subtle text-primary border border-border flex items-center justify-center flex-shrink-0">
                                  <GraduationCap size={17} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-[10px] font-bold uppercase tracking-[0.06em] text-primary">
                                    {getQualificationDisplayName(
                                      edu.qualification_type
                                    )}
                                  </div>
                                  <div className="text-[13.5px] font-[650] text-foreground mt-[2px]">
                                    {edu.institution || '—'}
                                  </div>
                                  <div className="text-[12px] text-muted-foreground mt-[3px] flex gap-[10px] flex-wrap">
                                    {edu.graduation_year && (
                                      <span>{edu.graduation_year}</span>
                                    )}
                                    {edu.board_type && (
                                      <span>{edu.board_type}</span>
                                    )}
                                    {edu.score_percentage != null &&
                                      edu.score_percentage !== '' && (
                                        <span className="font-semibold text-foreground">
                                          {formatScore(edu.score_percentage)}%
                                        </span>
                                      )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      <p className="text-[13px] text-muted-foreground text-center py-[20px] m-0">
                        No education added yet
                      </p>
                    )}
                  </div>
                </div>

                {/* Rail */}
                <div
                  style={{ position: 'sticky', top: 64 }}
                  className="flex flex-col gap-4 self-start"
                >
                  {/* Snapshot */}
                  <div className="bg-card border border-border rounded-xl p-[18px_20px]">
                    <div className="flex items-center gap-[9px] mb-[12px]">
                      <div className="w-[26px] h-[26px] rounded-[7px] bg-brand-bg-subtle text-primary flex items-center justify-center flex-shrink-0">
                        <User size={13} />
                      </div>
                      <h3 className="text-[14px] font-bold tracking-[-0.02em] text-foreground m-0">
                        Snapshot
                      </h3>
                    </div>
                    {snapshotRows.map(({ icon: Icon, label, value, sub }) => (
                      <div
                        key={label}
                        className="flex items-start gap-[11px] py-[11px] border-b border-border last:border-b-0"
                      >
                        <div className="w-[30px] h-[30px] rounded-[8px] bg-muted text-muted-foreground flex items-center justify-center flex-shrink-0">
                          <Icon size={14} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] text-muted-foreground font-semibold uppercase tracking-[0.04em]">
                            {label}
                          </div>
                          <div className="text-[13px] text-foreground font-semibold mt-[2px] overflow-hidden text-ellipsis whitespace-nowrap">
                            {value}
                            {sub && (
                              <span className="text-muted-foreground font-normal ml-[4px]">
                                {sub}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Skills */}
                  {skills?.length > 0 && (
                    <div
                      ref={(el) => {
                        sectionRefs.current.skills = el;
                      }}
                      id="section-skills"
                      className="bg-card border border-border rounded-xl p-[18px_20px]"
                    >
                      <div className="flex items-center gap-[9px] mb-[14px]">
                        <div className="w-[26px] h-[26px] rounded-[7px] bg-brand-bg-subtle text-primary flex items-center justify-center flex-shrink-0">
                          <Award size={13} />
                        </div>
                        <h3 className="text-[14px] font-bold tracking-[-0.02em] text-foreground m-0">
                          Skills
                        </h3>
                        <span className="font-mono text-[11px] font-semibold text-muted-foreground bg-muted border border-border px-[7px] py-[1px] rounded-[20px] ml-auto">
                          {skillsCount}
                        </span>
                      </div>
                      <div className="flex flex-col gap-3">
                        {(hasSkillTypes
                          ? [
                              { group: domainSkills, title: 'Domain' },
                              { group: toolSkills, title: 'Tools' },
                              { group: regSkills, title: 'Regulatory' },
                            ]
                          : [{ group: skills, title: null }]
                        )
                          .filter(({ group }) => group.length > 0)
                          .map(({ group, title }) => (
                            <div key={title || 'all'}>
                              {title && (
                                <div className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground mb-[7px]">
                                  {title}
                                </div>
                              )}
                              <div className="flex flex-wrap gap-[6px]">
                                {group.map((sk) => {
                                  const skName =
                                    typeof sk === 'string'
                                      ? sk
                                      : sk?.skill_name || sk?.name || '';
                                  const skKey =
                                    typeof sk === 'object' && sk?.id
                                      ? String(sk.id)
                                      : skName;
                                  return (
                                    <SkillPill key={skKey} label={skName} />
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Certifications */}
                  {certifications?.length > 0 && (
                    <div className="bg-card border border-border rounded-xl p-[18px_20px]">
                      <div className="flex items-center gap-[9px] mb-[14px]">
                        <div className="w-[26px] h-[26px] rounded-[7px] bg-ds-success-subtle text-ds-success border border-ds-success/20 flex items-center justify-center flex-shrink-0">
                          <Award size={13} />
                        </div>
                        <h3 className="text-[14px] font-bold tracking-[-0.02em] text-foreground m-0">
                          Certifications
                        </h3>
                        <span className="font-mono text-[11px] font-semibold text-muted-foreground bg-muted border border-border px-[7px] py-[1px] rounded-[20px] ml-auto">
                          {certifications.length}
                        </span>
                      </div>
                      <div>
                        {certifications.map((cert) => {
                          const certName =
                            typeof cert === 'string'
                              ? cert
                              : cert?.certification_name || cert?.name || '';
                          const certBody =
                            typeof cert === 'object'
                              ? cert?.issuing_body
                              : null;
                          const certCat =
                            typeof cert === 'object' ? cert?.category : null;
                          const certKey =
                            typeof cert === 'object' && cert?.id
                              ? String(cert.id)
                              : certName;
                          return (
                            <div
                              key={certKey}
                              className="flex items-start gap-[11px] py-[11px] border-b border-border last:border-b-0"
                            >
                              <div className="w-[32px] h-[32px] rounded-[8px] bg-ds-success-subtle text-ds-success border border-ds-success/20 flex items-center justify-center flex-shrink-0">
                                <Award size={14} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-[12.8px] font-[650] text-foreground">
                                  {certName}
                                </div>
                                {certBody && (
                                  <div className="text-[11.5px] text-muted-foreground mt-[2px]">
                                    {certBody}
                                  </div>
                                )}
                                {certCat && (
                                  <span className="inline-block mt-[5px] text-[9.5px] font-bold uppercase tracking-[0.04em] text-primary bg-brand-bg-subtle px-[7px] py-[2px] rounded-[5px]">
                                    {certCat}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {activeTab === 'audit' && (
            <>
              {/* Audit Log Filters */}
              <div className="bg-card border border-border rounded-xl p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <SearchInput
                    value={auditSearch}
                    onValueChange={setAuditSearch}
                    placeholder="Search in event / summary / description"
                  />
                  <AppSelect
                    value={eventCategory || 'all'}
                    onValueChange={(val) =>
                      setEventCategory(val === 'all' ? '' : val)
                    }
                    options={[
                      { value: 'all', label: 'All categories' },
                      ...Object.entries(auditConfig.event_categories || {}).map(
                        ([key, val]) => ({ value: key, label: val })
                      ),
                    ]}
                    placeholder="All categories"
                  />
                  <AppSelect
                    value={actorType || 'all'}
                    onValueChange={(val) =>
                      setActorType(val === 'all' ? '' : val)
                    }
                    options={[
                      { value: 'all', label: 'All actor types' },
                      ...Object.entries(auditConfig.actor_types || {}).map(
                        ([key, val]) => ({ value: key, label: val })
                      ),
                    ]}
                    placeholder="All actor types"
                  />
                  <AppSelect
                    value={sortBy}
                    onValueChange={setSortBy}
                    options={[
                      { value: 'newest', label: 'Newest first' },
                      { value: 'oldest', label: 'Oldest first' },
                    ]}
                    placeholder="Newest first"
                  />
                </div>
                <div className="flex items-center justify-between mt-3">
                  <p className="text-[12.5px] text-muted-foreground">
                    Showing{' '}
                    <span className="font-semibold text-foreground-strong">
                      {auditLogs.length}
                    </span>{' '}
                    event{auditLogs.length === 1 ? '' : 's'}
                    {auditLogs.length > 0 && (
                      <>
                        {' '}
                        · Latest:{' '}
                        {formatAuditDate(auditLogs[0]?.event_timestamp)}
                      </>
                    )}
                  </p>
                </div>
              </div>

              {/* Audit Log List */}
              {auditLogsLoading && (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              )}

              {!auditLogsLoading && auditLogs.length === 0 && (
                <div className="bg-card border border-border rounded-xl p-8 text-center mt-4">
                  <p className="text-[13px] text-muted-foreground">
                    No audit logs found
                  </p>
                </div>
              )}

              {!auditLogsLoading && auditLogs.length > 0 && (
                <div className="flex flex-col gap-3 mt-4">
                  {auditLogs.map((log) => (
                    <div
                      key={log.id}
                      className="bg-card border border-border rounded-xl p-4"
                    >
                      <div className="flex items-start gap-3.5">
                        <div className="w-9 h-9 rounded-full bg-brand-bg-subtle text-primary flex items-center justify-center font-semibold text-[13px] flex-shrink-0">
                          {getEventIcon(log.event_label)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-3 mb-1">
                            <h4 className="text-[13.5px] font-[650] text-foreground-strong">
                              {log.event_label}
                            </h4>
                            <span className="text-[11.5px] text-muted-foreground whitespace-nowrap">
                              {formatAuditDate(log.event_timestamp)}
                            </span>
                          </div>
                          {log.event_description && (
                            <p className="text-[12.5px] text-muted-foreground mb-2">
                              {log.event_description}
                            </p>
                          )}
                          <div className="flex gap-[6px] flex-wrap">
                            {log.event_category && (
                              <span className="px-[9px] py-[3px] rounded-full text-[11px] font-medium bg-brand-bg-subtle text-primary border border-primary/15">
                                {log.event_category.replace(/_/g, ' ')}
                              </span>
                            )}
                            {log.actor_type && (
                              <span className="px-[9px] py-[3px] rounded-full text-[11px] font-medium bg-muted text-muted-foreground border border-border">
                                {log.actor_type}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {(auditPagination.totalPages || 1) > 1 && (
                    <div className="flex items-center justify-center gap-2 pt-1">
                      <button
                        type="button"
                        disabled={!auditPagination.hasPrevious}
                        onClick={() => setAuditPage((p) => Math.max(1, p - 1))}
                        className="w-8 h-8 rounded-lg border border-border bg-card text-muted-foreground disabled:opacity-40 hover:enabled:border-primary hover:enabled:text-primary transition-colors inline-flex items-center justify-center"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <span className="text-[12.5px] text-muted-foreground font-medium">
                        Page {auditPagination.page || auditPage} of{' '}
                        {auditPagination.totalPages}
                      </span>
                      <button
                        type="button"
                        disabled={!auditPagination.hasNext}
                        onClick={() => setAuditPage((p) => p + 1)}
                        className="w-8 h-8 rounded-lg border border-border bg-card text-muted-foreground disabled:opacity-40 hover:enabled:border-primary hover:enabled:text-primary transition-colors inline-flex items-center justify-center"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          <Dialog open={showPdfDialog} onOpenChange={setShowPdfDialog}>
            <DialogContent className="max-w-[380px]">
              <DialogHeader>
                <DialogTitle>Export Resume PDF</DialogTitle>
              </DialogHeader>
              <div
                role="button"
                tabIndex={0}
                className="bg-muted/40 px-4 py-3 flex items-center gap-3 cursor-pointer select-none"
                onClick={() => setPdfMasked((v) => !v)}
                onKeyDown={(e) => e.key === ' ' && setPdfMasked((v) => !v)}
              >
                <Checkbox
                  checked={pdfMasked}
                  aria-label="Mask personal details"
                />
                <div>
                  <div className="text-[13px] font-[600] text-foreground-strong leading-tight">
                    Mask personal details
                  </div>
                  <div className="text-[12px] text-muted-foreground mt-[3px]">
                    Hides contact info and sensitive fields from the PDF
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowPdfDialog(false);
                    setPdfMasked(false);
                  }}
                  disabled={pdfLoading}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleDownloadPdf}
                  disabled={pdfLoading}
                  className="gap-1.5"
                >
                  {pdfLoading ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <FileDown className="w-3.5 h-3.5" strokeWidth={1.8} />
                  )}
                  {pdfLoading ? 'Downloading…' : 'Download PDF'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <JoiningDetailsModal
            isOpen={showJoiningModal}
            onClose={() => setShowJoiningModal(false)}
            onSubmit={handleJoiningDetailsSubmit}
            candidateId={id}
            opportunityId={opportunityId}
            initialJoiningDate={joiningInfo?.joining_date}
            initialCtc={joiningInfo?.ctc_amount}
          />
        </div>
      </div>
    </div>
  );
};

export default Preview;
